import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * Hook для управления фильтрами
 *
 * @param {number} categoryId - ID категории
 * @returns {Object} - Объект с состоянием и методами для работы с фильтрами
 */
export const useFilters = (categoryId) => {
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Ключ для sessionStorage
  const storageKey = `filters_category_${categoryId}`;

  /**
   * Загрузить теги для категории
   */
  const loadTagsForCategory = useCallback(async (catId) => {
    if (!catId) {
      setAvailableTags([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/categories/${catId}/tags`);
      setAvailableTags(response.data.data || []);
    } catch (err) {
      setError(err.message);
      setAvailableTags([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Сохранить выбранные теги в sessionStorage
   */
  const saveToStorage = useCallback((tagIds) => {
    if (categoryId) {
      sessionStorage.setItem(storageKey, JSON.stringify(tagIds));
    }
  }, [categoryId, storageKey]);

  /**
   * Переключить тег (добавить/удалить)
   */
  const toggleTag = useCallback((tagId) => {
    setSelectedTagIds(prev => {
      const newArray = prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId];
      saveToStorage(newArray);
      return newArray;
    });
  }, [saveToStorage]);

  /**
   * Очистить все выбранные теги
   */
  const clearTags = useCallback(() => {
    setSelectedTagIds([]);
    if (categoryId) {
      sessionStorage.removeItem(storageKey);
    }
  }, [categoryId, storageKey]);

  /**
   * Проверить выбран ли тег
   */
  const isTagSelected = useCallback((tagId) => {
    return selectedTagIds.includes(tagId);
  }, [selectedTagIds]);

  /**
   * Получить количество выбранных тегов
   */
  const getSelectedCount = useCallback(() => {
    return selectedTagIds.length;
  }, [selectedTagIds]);

  /**
   * Установить выбранные теги (для начальной загрузки)
   */
  const setSelectedTags = useCallback((tagIds) => {
    setSelectedTagIds(tagIds);
    saveToStorage(tagIds);
  }, [saveToStorage]);

  // Загрузка тегов при изменении категории
  useEffect(() => {
    if (categoryId) {
      loadTagsForCategory(categoryId);
      // Загрузить сохраненные фильтры
      try {
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          setSelectedTagIds(parsed);
        } else {
          setSelectedTagIds([]);
        }
      } catch (err) {
        setSelectedTagIds([]);
      }
    } else {
      setAvailableTags([]);
      setSelectedTagIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, storageKey]);

  // Очистка фильтров при размонтировании или смене категории
  useEffect(() => {
    return () => {
      // Не очищаем при размонтировании, только при смене категории
    };
  }, []);

  return {
    selectedTagIds,
    availableTags,
    loading,
    error,
    toggleTag,
    clearTags,
    isTagSelected,
    getSelectedCount,
    setSelectedTags,
    loadTagsForCategory,
  };
};

export default useFilters;
